import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { TPEChatMessage, TPEChatSession } from '../types';

interface TPEChatState {
  // Current session
  currentSession: TPEChatSession | null;
  
  // Messages for current session
  messages: TPEChatMessage[];
  
  // Chat input
  message: string;
  
  // Loading states
  isGenerating: boolean;
  isLoadingHistory: boolean;
  
  // Error states
  error: string | null;
  
  // Context information
  questionnaireId?: string;
  vendorId?: string;
  token?: string;
  chatId?: string; // Track current chat_id for socket events
  
  // Actions
  setCurrentSession: (session: TPEChatSession | null) => void;
  setMessages: (messages: TPEChatMessage[]) => void;
  addMessage: (message: TPEChatMessage) => void;
  updateMessage: (messageId: string, updates: Partial<TPEChatMessage>) => void;
  setMessage: (message: string) => void;
  setIsGenerating: (isGenerating: boolean) => void;
  setIsLoadingHistory: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setContext: (context: { questionnaireId?: string; vendorId?: string; token?: string; chatId?: string }) => void;
  clearChat: () => void;
}

export const useTPEChatStore = create<TPEChatState>()(
  devtools(
    (set, get) => ({
      // Initial state
      currentSession: null,
      messages: [],
      message: '',
      isGenerating: false,
      isLoadingHistory: false,
      error: null,
      questionnaireId: undefined,
      vendorId: undefined,
      token: undefined,
      chatId: undefined,
      
      // Actions
      setCurrentSession: (session) => set({ currentSession: session }),
      
      setMessages: (messages) => set({ messages }),
      
      addMessage: (message) => set((state) => ({
        messages: [...state.messages, message]
      })),
      
      updateMessage: (messageId, updates) => set((state) => ({
        messages: state.messages.map(msg => 
          msg.id === messageId ? { ...msg, ...updates } : msg
        )
      })),
      
      setMessage: (message) => set({ message }),
      
      setIsGenerating: (isGenerating) => set({ isGenerating }),
      
      setIsLoadingHistory: (isLoading) => set({ isLoadingHistory: isLoading }),
      
      setError: (error) => set({ error }),
      
      setContext: (context) => set((state) => ({
        ...state,
        ...context
      })),
      
      clearChat: () => set({
        currentSession: null,
        messages: [],
        message: '',
        isGenerating: false,
        isLoadingHistory: false,
        error: null
      }),
    }),
    {
      name: 'tpe-chat-store',
    }
  )
);